/*
Genre info loader — mirrors Android GenreInfo.kt (introduced in Android
v1.1.0 « Catalogue »). Loads /data/genre-info.json (an array of entries
keyed by latin genus `g`) and exposes a Map<string, GenreEntry> for the
GenreDetailScreen + the Arboretum « Par genre » header chapters.

The asset is shipped at build time by the Android `tools/build_dataset.py`
pipeline. 204 entries at the v1.3.2 tag — 200 genres with at least one
identified species + 3 only-unknown (Genista, Vitex, Ziziphus) +
one more flavour the script may add. Older PWA datasets (pre-v1.3.2)
won't carry this file; the loader throws on miss so the consumer can
fall back.
*/

import { fetchJson } from './fetchJson'

const kAssetPath = '/data/genre-info.json'  // canonical location served by the SW

export interface GenreTopSpecies
{
	sk: number  // speciesIndex of one of the genus's most-frequent identified species
	nv: string  // vernacular display name (cascade Wikidata → editorial overrides)
	count: number  // arbres of that species at Paris
}

export interface GenreTopArr
{
	arr: string  // arrondissement label, e.g. "5e" or "Bois de Boulogne"
	count: number
}

export interface GenreTopArrOver
{
	arr: string
	ratio: number  // share of the genre concentrated in this arr relative to dataset baseline
	count: number
}

export interface GenreStats
{
	count: number  // total arbres of the genus at Paris
	speciesIdentified: number  // number of identified species of the genus (excludes (G, sp.))
	topSpecies: GenreTopSpecies[]  // top 3 identified species by count
	topArr: GenreTopArr[]  // top 3 arrondissements by absolute count
	proportion?: number  // share of the dataset, 0..1 (optional on tiny genres)
	medianHm?: number  // median height in m (optional)
	medianCircCm?: number  // median circumference in cm (optional)
	topArrOver?: GenreTopArrOver[]  // top 3 arrondissements by overrepresentation ratio (optional)
}

export interface GenreEntry
{
	g: string  // latin genus, the dictionary key (also stored on the entry for self-contained reads)
	fr?: string  // French display name (« Érable »), absent when no editorial label exists
	wp?: string  // Wikipedia FR slug (« Acer »), used to build the article link
	summary?: string  // first paragraph of the Wikipedia FR article
	stats: GenreStats
}

let fEntries: Map<string, GenreEntry> | null = null  // memoized genus -> entry, null until first load

/*
Ensure genre-info is loaded and indexed by latin genus. Idempotent — only
the first call hits the network. Throws on fetch failure; the
GenreDetailScreen falls back to a redirect-map empty state in that case.
*/
export async function ensureGenreInfo(): Promise<Map<string, GenreEntry>>
{
	if (fEntries) return fEntries
	const vArr = await fetchJson<GenreEntry[]>(kAssetPath)  // throws with context on bad JSON
	const vMap = new Map<string, GenreEntry>()  // built then handed to module singleton
	for (const vEntry of vArr) vMap.set(vEntry.g, vEntry)
	fEntries = vMap
	return fEntries
}

/*
Look up the entry for a given latin genus. Returns null if not loaded yet
or unknown — caller treats this as a soft miss (the GenreDetailScreen
shows identity + catalogue but skips the Wikipedia/stats blocks).
*/
export function genreInfoFor(inGenre: string): GenreEntry | null
{
	if (!fEntries) return null
	return fEntries.get(inGenre) ?? null
}
